import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { LOGO_URL } from "../assets/images";
import PasswordInput from "./PasswordInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Guard: if there's no token in the URL, show error immediately.
  const noToken = !token;

  useEffect(() => {
    if (done) {
      const t = setTimeout(() => navigate("/membros/login", { replace: true }), 3500);
      return () => clearTimeout(t);
    }
  }, [done, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("A palavra-passe deve ter no mínimo 8 caracteres."); return; }
    if (password !== confirm) { toast.error("As palavras-passe não coincidem."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/members/reset-password`, { token, password });
      setDone(true);
      toast.success("Palavra-passe atualizada.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível atualizar. O link pode ter expirado.";
      toast.error(typeof msg === "string" ? msg : "Erro.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))] flex items-center justify-center p-6" data-testid="member-reset-page">
      <div className="w-full max-w-md bg-white border border-[hsl(var(--border))] p-10 sm:p-12">
        <a href="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="CIEES" className="h-14 w-14" />
          <div>
            <div className="display-heading text-xl text-[hsl(var(--primary))]">CIEES Portugal</div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[hsl(var(--muted-foreground))]">Nova palavra-passe</div>
          </div>
        </a>

        {noToken ? (
          <div className="mt-10" data-testid="member-reset-no-token">
            <AlertTriangle className="h-10 w-10 text-[hsl(var(--accent))]" />
            <h1 className="display-heading text-3xl mt-5 leading-[1.1]">Link inválido.</h1>
            <span className="accent-rule mt-6 block" />
            <p className="mt-6 text-[hsl(var(--muted-foreground))] leading-relaxed">
              Este endereço deve ser aberto a partir do link enviado por email. Se o seu link expirou, peça um novo.
            </p>
            <Link to="/membros/esqueci" className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-[hsl(var(--primary))] font-medium">
              Pedir novo link →
            </Link>
          </div>
        ) : done ? (
          <div className="mt-10" data-testid="member-reset-success">
            <CheckCircle2 className="h-10 w-10 text-[hsl(var(--primary))]" />
            <h1 className="display-heading text-3xl mt-5 leading-[1.1]">Palavra-passe atualizada.</h1>
            <span className="accent-rule mt-6 block" />
            <p className="mt-6 text-[hsl(var(--muted-foreground))] leading-relaxed">
              A sua nova palavra-passe está ativa. Vai ser redirecionado para o login.
            </p>
            <Link to="/membros/login" className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-[hsl(var(--primary))] font-medium">
              Entrar agora →
            </Link>
          </div>
        ) : (
          <>
            <h1 className="display-heading text-3xl mt-10 leading-[1.1]">Defina uma nova palavra-passe.</h1>
            <span className="accent-rule mt-6 block" />
            <p className="mt-6 text-[hsl(var(--muted-foreground))] leading-relaxed text-[15px]">
              Escolha uma palavra-passe com pelo menos <strong>8 caracteres</strong>. Recomendamos misturar maiúsculas, números e símbolos.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="member-reset-form">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">Nova palavra-passe</label>
                <PasswordInput
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[hsl(var(--border))] py-2.5 px-3 text-[15px] focus:outline-none focus:border-[hsl(var(--accent))]"
                  testIdPrefix="member-reset-password"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">Confirmar palavra-passe</label>
                <PasswordInput
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full bg-white border border-[hsl(var(--border))] py-2.5 px-3 text-[15px] focus:outline-none focus:border-[hsl(var(--accent))]"
                  testIdPrefix="member-reset-confirm"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="member-reset-submit">
                {loading ? "A atualizar…" : "Atualizar palavra-passe"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>

            <div className="mt-7 flex items-center justify-between text-sm">
              <Link to="/membros/login" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">← Voltar ao login</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
